import { useAccess } from '../hooks/useAccess'
import { CompanyMeasurementProvider } from '../context/CompanyMeasurementProvider'
import { Measure, Dimensions } from '../components/Measurement'
import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { Product } from '../services/product.service'
import { apiFetch } from '../utils/apiFetch'
import { adrReferenceService } from '../services/adrReference.service'
import StarRating from '../components/ui/StarRating'
import {
  Layers,
  Thermometer,
  Rotate3D,
  LayerArrowUp,
  LayerArrowDown,
  Table
} from 'lucide-react'
import {
  CubeIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  ListBulletIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline'
import ProductForm from '../components/ProductForm'
import ProductExportButton from '../components/ProductExportButton'
import ProductImportButton from '../components/ProductImportButton'

// ─── Constraint Badges Component ─────────────────────────────────────────────
function ConstraintBadges({ product, showLabels = false }: { product: Product; showLabels?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {/* Empilable / Non-Empilable */}
      <span
        title={product.stackable === true ? "Empilable (Superposition autorisée)" : "Non-Empilable (Ne pas superposer d'autres marchandises)"}
        className={`inline-flex items-center justify-center gap-1 p-1 rounded-md ${product.stackable === true
          ? 'text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40'
          : 'text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60'
        }`}
      >
        <Layers className={`w-4 h-4 flex-shrink-0 ${product.stackable === true ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400 dark:text-slate-500'}`} />
        {showLabels && <span className="text-[10px] font-extrabold uppercase tracking-wider pr-0.5">{product.stackable === true ? 'EMPILABLE' : 'NON-EMP.'}</span>}
      </span>

      {/* Au sol uniquement */}
      <span
        title={product.must_be_on_floor
          ? "Au Sol Uniquement (Obligation de poser au plancher)"
          : "Pas d'obligation au sol"}
        className={`inline-flex items-center justify-center gap-1 p-1 rounded-md ${product.must_be_on_floor
          ? 'text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/40'
          : 'text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60'
        }`}
      >
        <LayerArrowDown className={`w-4 h-4 flex-shrink-0 ${product.must_be_on_floor ? 'text-purple-600 dark:text-purple-400' : 'text-slate-400 dark:text-slate-500'}`} />
        {showLabels && <span className="text-[10px] font-extrabold uppercase tracking-wider pr-0.5">{product.must_be_on_floor ? 'AU SOL' : 'NON-SOL'}</span>}
      </span>

      {/* Rien au-dessus */}
      <span
        title={product.no_stack_above ? "Rien au-dessus (Ne peut pas être en dessous d'autres marchandises)" : "Peut supporter des marchandises au-dessus"}
        className={`inline-flex items-center justify-center gap-1 p-1 rounded-md ${product.no_stack_above
          ? 'text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/40'
          : 'text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60'
        }`}
      >
        <LayerArrowUp className={`w-4 h-4 flex-shrink-0 ${product.no_stack_above ? 'text-orange-600 dark:text-orange-400' : 'text-slate-400 dark:text-slate-500'}`} />
        {showLabels && <span className="text-[10px] font-extrabold uppercase tracking-wider pr-0.5">{product.no_stack_above ? 'RIEN DESSUS' : 'SUPPORT'}</span>}
      </span>

      {/* Rotatable */}
      <span
        title={product.rotatable ? "Rotation Autorisée lors du chargement" : "Rotation Non Autorisée"}
        className={`inline-flex items-center justify-center gap-1 p-1 rounded-md ${product.rotatable
          ? 'text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40'
          : 'text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60'
        }`}
      >
        <Rotate3D className={`w-4 h-4 flex-shrink-0 ${product.rotatable ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500'}`} />
        {showLabels && <span className="text-[10px] font-extrabold uppercase tracking-wider pr-0.5">{product.rotatable ? 'ROTATABLE' : 'NON-ROT.'}</span>}
      </span>

      {/* Palettiser automatiquement */}
      <span
        title={product.requires_pallet ? "Palettiser automatiquement (Génération automatique de palette)" : "Pas de palettisation automatique"}
        className={`inline-flex items-center justify-center gap-1 p-1 rounded-md ${product.requires_pallet
          ? 'text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40'
          : 'text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60'
        }`}
      >
        <Table className={`w-4 h-4 flex-shrink-0 ${product.requires_pallet ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`} />
        {showLabels && <span className="text-[10px] font-extrabold uppercase tracking-wider pr-0.5">{product.requires_pallet ? 'AUTO-PAL' : 'MANUEL'}</span>}
      </span>

      {/* Température / Frigo */}
      <span
        title={product.temperature_controlled
          ? `Contrôle de Température (${product.min_temperature ?? '?'}°C à ${product.max_temperature ?? '?'}°C)`
          : 'Pas de Contrôle de Température'}
        className={`inline-flex items-center justify-center gap-1 p-1 rounded-md ${product.temperature_controlled
          ? 'text-cyan-700 dark:text-cyan-300 border border-cyan-300 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950/40'
          : 'text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60'
        }`}
      >
        <Thermometer className={`w-4 h-4 flex-shrink-0 ${product.temperature_controlled ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-400 dark:text-slate-500'}`} />
        {showLabels && <span className="text-[10px] font-extrabold uppercase tracking-wider pr-0.5">{product.temperature_controlled ? 'FROID' : 'NON-FROID'}</span>}
      </span>

      {/* ADR / Dangereux */}
      <span
        title={product.is_dangerous_goods
          ? `ADR - Matière Dangereuse${product.dangerous_goods_class ? ' (Classe ' + product.dangerous_goods_class + ')' : ''}${product.un_number ? ' UN ' + product.un_number : ''}`
          : 'Non Dangereux'}
        className={`hazmat-stripe inline-flex items-center justify-center gap-1 p-1 rounded-md ${product.is_dangerous_goods
          ? 'text-red-700 dark:text-red-300 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40'
          : 'text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60'
        }`}
      >
        <ExclamationTriangleIcon className={`w-4 h-4 flex-shrink-0 ${product.is_dangerous_goods ? 'text-red-600 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`} />
        {showLabels && <span className="text-[10px] font-black uppercase tracking-wider pr-0.5">{product.is_dangerous_goods ? 'ADR' : 'NON-ADR'}</span>}
      </span>
    </div>
  )
}


// ─── Delete Confirmation Dialog ──────────────────────────────────────────────
function DeleteDialog({ name, onConfirm, onCancel }: { name: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-red-100 dark:border-red-900/40">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Supprimer le produit</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Cette action est irréversible.</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
          Voulez-vous supprimer <strong className="text-gray-900 dark:text-white">"{name}"</strong> ?
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Annuler
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors">
            Supprimer
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Product Card ─────────────────────────────────────────────────────────────
function ProductCard({ product, onEdit, onDelete, onDuplicate, rights }: { product: Product; onEdit: () => void; onDelete: () => void; onDuplicate: () => void; rights: { create: boolean; update: boolean; delete: boolean } }) {
  const color = product.color || '#6366f1'
  const dims = product.length && product.width && product.height
    ? <Dimensions length={product.length} width={product.width} height={product.height} from={product.dimension_unit || 'mm'} />
    : null

  return (
    <div className="shipping-label cargo-corner group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-xl hover:shadow-orange-500/10 hover:-translate-y-1 transition-all duration-200 overflow-hidden">
      {/* Cargo color stripe + barcode-style header */}
      <div className="flex items-stretch min-w-0">
        <div className="w-2 flex-shrink-0" style={{ background: `linear-gradient(180deg, ${color}, ${color}99)` }} />
        <div className="flex-1 min-w-0 overflow-hidden">
          {/* Header row: Product ID chip */}
          <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-2 border-b border-dashed border-slate-200 dark:border-slate-700">
            <div className="flex items-start gap-2.5 min-w-0 flex-1 overflow-hidden">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '18', border: `1.5px solid ${color}50` }}>
                <CubeIcon className="w-4.5 h-4.5" style={{ color }} />
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <div className="flex items-center justify-between gap-2 min-w-0 w-full">
                  <p className="font-bold text-slate-900 dark:text-white truncate text-sm min-w-0 w-0 flex-1 overflow-hidden">{product.name}</p>
                  <span className={`ml-auto px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0 uppercase tracking-wider border ${product.status === 'active'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
                    : product.status === 'draft'
                      ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800'
                      : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                    }`}>
                    {product.status === 'active' ? '● Actif' : product.status === 'draft' ? '◐ Draft' : '○ Inactif'}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 min-w-0 w-full">
                  <p className="text-[10px] text-slate-400 font-mono tracking-widest truncate min-w-0 w-0 flex-1 overflow-hidden">{product.sku || '—'}</p>
                  <div className="flex-shrink-0 ml-2">
                    <StarRating rating={product.favorite_rating || 0} readonly size="sm" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 py-3 space-y-3">
            {/* Dimension specs — manifeste style */}
            {(dims || product.weight) && (
              <div className="grid grid-cols-2 gap-2">
                {dims && (
                  <div className="bg-slate-50 dark:bg-slate-800/60 rounded border border-slate-100 dark:border-slate-700 px-2.5 py-1.5">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">L×l×H</p>
                    <p className="text-xs font-mono font-semibold text-slate-700 dark:text-slate-200 truncate">{dims}</p>
                  </div>
                )}
                {product.weight && (
                  <div className="bg-slate-50 dark:bg-slate-800/60 rounded border border-slate-100 dark:border-slate-700 px-2.5 py-1.5">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Masse</p>
                    <p className="text-xs font-mono font-semibold text-slate-700 dark:text-slate-200"><Measure value={product.weight} from={product.weight_unit || 'kg'} quantity="mass" /></p>
                  </div>
                )}
              </div>
            )}

            {/* Classification badges */}
            <ConstraintBadges product={product} />

            {/* Actions */}
            <div className="flex gap-1.5 pt-2 border-t border-dashed border-slate-200 dark:border-slate-700">
              <button disabled={!rights.update} onClick={onEdit} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors border border-transparent hover:border-orange-200 dark:hover:border-orange-800">
                <PencilIcon className="w-3 h-3" />Éditer
              </button>
              <button disabled={!rights.create} onClick={onDuplicate} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold text-slate-400 dark:text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border border-transparent hover:border-blue-200 dark:hover:border-blue-800">
                <DocumentDuplicateIcon className="w-3 h-3" />Dupliquer
              </button>
              <button disabled={!rights.delete} onClick={onDelete} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border border-transparent hover:border-red-200 dark:hover:border-red-800">
                <TrashIcon className="w-3 h-3" />Retirer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

// ProductModal moved to reusable component `ProductForm`

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProductSettings() {
  return <CompanyMeasurementProvider><ProductSettingsContent /></CompanyMeasurementProvider>
}

function ProductSettingsContent() {
  const access = useAccess()
  const rights = access.resource('product')
  const canWrite = rights.update
  const { user } = useAuth()
  const tenantId = user?.tenantId ?? user?.tenant_id ?? null
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([])
  const queryClient = useQueryClient()

  const toggleAttribute = (attrId: string) => {
    setSelectedAttributes(prev =>
      prev.includes(attrId) ? prev.filter(a => a !== attrId) : [...prev, attrId]
    )
  }

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg })
    setTimeout(() => setToast(null), 3500)
  }

  const { data: productsData, isLoading } = useQuery({
    queryKey: ['products', tenantId ?? `user:${user?.id ?? 'unknown'}`],
    enabled: rights.read,
    queryFn: async () => {
      const response = await apiFetch('/api/internal/tms/products?page=1&pageSize=100')
      const data = await response.json()

      // Gérer différentes structures de réponse possibles
      const products = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.products)
          ? data.products
          : Array.isArray(data)
            ? data
            : []

      return { data: products }
    }
  })

  // Fetch ADR classes for select
  const { data: adrClasses = [] } = useQuery({
    queryKey: ['adr-classes'],
    enabled: rights.read && access.has('reference_data:read'),
    queryFn: () => adrReferenceService.getADRClasses()
  })

  // Fetch UN numbers for select
  const { data: unNumbers = [] } = useQuery({
    queryKey: ['un-numbers'],
    enabled: rights.read && access.has('reference_data:read'),
    queryFn: () => adrReferenceService.getUNNumbers()
  })

  const createMutation = useMutation({
    mutationFn: async (product: Omit<Product, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>) => {
      const payload = normalizeProductPayload(product)
      const response = await apiFetch('/api/internal/tms/products', { method: 'POST', body: JSON.stringify(payload) })
      return response.json()
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }); setIsModalOpen(false); setEditingProduct(null); showToast('success', 'Produit créé avec succès.') }
  })

  const numericFields = [
    'quantity', 'weight', 'length', 'width', 'height', 'volume',
    'max_stack_weight', 'max_stack_layer', 'min_support_ratio', 'max_pallet_height_mm', 'pallet_board_height_mm', 'pallet_empty_weight_kg', 'max_pallet_weight_kg',
    'min_temperature', 'max_temperature', 'pallet_custom_length', 'pallet_custom_width'
  ] as const

  type NumericField = (typeof numericFields)[number]

  const normalizeProductPayload = (product: Partial<Product>): Partial<Product> => {
    const normalized = { ...product } as Partial<Product> & Record<NumericField, number | null | undefined>

    numericFields.forEach(field => {
      const value = product[field]
      if (value === null || value === undefined) {
        normalized[field] = null
        return
      }

      if (typeof value === 'string') {
        if (value === '') {
          normalized[field] = null
          return
        }

        const parsed = ['quantity', 'max_stack_layer', 'max_pallet_height_mm', 'pallet_board_height_mm'].includes(field)
          ? parseInt(value, 10)
          : parseFloat(value)

        normalized[field] = Number.isNaN(parsed) ? null : parsed
      }
    })

    return normalized
  }

  const updateMutation = useMutation({
    mutationFn: async ({ id, product }: { id: string; product: Partial<Product> }) => {
      const payload = normalizeProductPayload(product)
      const response = await apiFetch(`/api/internal/tms/products/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
      return response.json()
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }); setIsModalOpen(false); setEditingProduct(null); showToast('success', 'Produit mis à jour.') }
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => { await apiFetch(`/api/internal/tms/products/${id}`, { method: 'DELETE' }) },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }); setDeleteTarget(null); showToast('success', 'Produit supprimé.') }
  })

  const duplicateMutation = useMutation({
    mutationFn: async (product: Product) => {
      const { id, tenant_id, created_at, updated_at, ...productToDuplicate } = product
      const payload = normalizeProductPayload({
        ...productToDuplicate,
        name: `${product.name} (copie)`,
        sku: product.sku ? `${product.sku}-copy` : undefined
      })
      const response = await apiFetch('/api/internal/tms/products', { method: 'POST', body: JSON.stringify(payload) })
      return response.json()
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }); showToast('success', 'Produit dupliqué avec succès.') }
  })

  const allProducts: Product[] = rights.read ? productsData?.data || [] : []

  const filtered = useMemo(() => {
    return allProducts.filter(p => {
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').toLowerCase().includes(search.toLowerCase())
      const matchStatus = filterStatus === 'all' || p.status === filterStatus || (filterStatus === 'favorites' && (p.favorite_rating || 0) > 0)

      let matchAttr = true
      if (selectedAttributes.length > 0) {
        matchAttr = selectedAttributes.every(attr => {
          if (attr === 'dangerous') return p.is_dangerous_goods === true
          if (attr === 'cold') return p.temperature_controlled === true
          if (attr === 'non_stackable') return p.stackable === false || p.no_stack_above === true
          if (attr === 'must_be_on_floor') return p.must_be_on_floor === true
          if (attr === 'rotatable') return p.rotatable === true
          return true
        })
      }

      return matchSearch && matchStatus && matchAttr
    })
  }, [allProducts, search, filterStatus, selectedAttributes])

  const stats = useMemo(() => ({
    total: allProducts.length,
    active: allProducts.filter(p => p.status === 'active').length,
    dangerous: allProducts.filter(p => p.is_dangerous_goods).length,
    cold: allProducts.filter(p => p.temperature_controlled).length,
    favorites: allProducts.filter(p => (p.favorite_rating || 0) > 0).length,
  }), [allProducts])

  if (!rights.read && !access.isLoading) return <p role="alert">La consultation du catalogue nécessite la permission « Voir les produits » et un accès en lecture.</p>

  if (isLoading || access.isLoading) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-4">
        <div className="w-20 h-20 cargo-bg rounded-2xl flex items-center justify-center shadow-xl border border-slate-700">
          <CubeIcon className="w-9 h-9 text-orange-400 animate-pulse" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest">Chargement manifeste…</p>
          <p className="text-xs text-slate-400 mt-1">Récupération du catalogue produits</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-0">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[70] flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl animate-fade-in-down border-l-4 ${toast.type === 'success'
          ? 'bg-slate-900 border-l-emerald-500 text-emerald-400'
          : 'bg-slate-900 border-l-red-500 text-red-400'
          }`}>
          <CheckCircleIcon className="w-5 h-5" />
          <span className="text-sm font-mono font-semibold text-white">{toast.msg}</span>
          <button onClick={() => setToast(null)} className="ml-2 opacity-50 hover:opacity-100 text-white"><XMarkIcon className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── CARGO HEADER BANNER ── */}
      <div className="cargo-bg rounded-none sm:rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm relative z-20 mb-6">
        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-orange-500 via-amber-400 to-orange-600 rounded-t-none sm:rounded-t-2xl" />
        <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            {/* Icon cargo container */}
            <div className="relative w-14 h-14 rounded-xl border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center flex-shrink-0">
              <CubeIcon className="w-7 h-7 text-orange-600 dark:text-orange-400" />
              <div className="absolute -top-1 -right-1 w-4.5 h-4.5 rounded-full bg-orange-500 flex items-center justify-center shadow-sm">
                <span className="text-[8px] font-black text-white">3D</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase">Catalogue Produits</h1>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded border border-orange-300 dark:border-orange-500/40 bg-orange-50/50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 uppercase tracking-widest">TMS</span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 font-mono">
                Référentiel articles
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <fieldset disabled={!rights.create} className="flex items-center gap-3">
            <ProductImportButton
              existingProducts={allProducts}
              onImportSuccess={(msg) => showToast('success', msg)}
            />
            </fieldset>
            <fieldset disabled={!rights.read} className="flex items-center gap-3">
            <ProductExportButton
              products={filtered}
              allProducts={allProducts}
              searchQuery={search}
              filterStatus={filterStatus}
              selectedAttributes={selectedAttributes}
              onExportSuccess={(msg) => showToast('success', msg)}
            />
            </fieldset>
            <button disabled={!rights.create} onClick={() => { setEditingProduct(null); setIsModalOpen(true) }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold text-sm uppercase tracking-wide cargo-glow hover:-translate-y-0.5 transition-all">
              <PlusIcon className="w-4 h-4" />
              Nouveau produit
            </button>
          </div>
        </div>
      </div>

      <div className="px-0 sm:px-0 space-y-5 warehouse-grid">

        {/* Stats — manifeste style */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Références', value: stats.total, icon: CubeIcon, accent: '#f97316', sub: 'catalogue total' },
            { label: 'Actifs', value: stats.active, icon: CheckCircleIcon, accent: '#10b981', sub: 'en production' },
            { label: 'Cl. ADR', value: stats.dangerous, icon: ExclamationTriangleIcon, accent: '#ef4444', sub: 'matières dangereuses' },
            { label: 'Frigorifiques', value: stats.cold, icon: Thermometer, accent: '#06b6d4', sub: 'chaîne du froid' },
            { label: 'Favoris', value: stats.favorites, icon: null, accent: '#fbbf24', sub: 'marqués favoris', isStar: true },
          ].map(s => {
            const Icon = s.icon
            return (
              <div key={s.label} className="relative cargo-corner bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 overflow-hidden">
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 19px, currentColor 20px)', backgroundSize: '100% 20px' }} />
                <div className="relative">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.accent + '18', border: `1.5px solid ${s.accent}40` }}>
                      {s.isStar ? (
                        <span className="text-amber-400 text-xl">⭐</span>
                      ) : Icon ? (
                        <Icon className="w-4.5 h-4.5" style={{ color: s.accent }} />
                      ) : null}
                    </div>
                    <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: s.accent }}>■</span>
                  </div>
                  <p className="text-3xl font-black text-slate-900 dark:text-white font-mono" style={{ textShadow: `0 0 20px ${s.accent}30` }}>{s.value}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mt-0.5">{s.label}</p>
                  <p className="text-[10px] text-slate-400 dark:text-slate-600">{s.sub}</p>
                </div>
              </div>
            )
          })}
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Recherche: nom, SKU, référence…"
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm font-mono text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition-all placeholder:text-slate-400" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-orange-500/40 transition-all">
              <option value="all">Tous statuts</option>
              <option value="active">● Actifs</option>
              <option value="inactive">○ Inactifs</option>
              <option value="draft">◐ Brouillons</option>
              <option value="favorites">⭐ Favoris</option>
            </select>
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button onClick={() => setViewMode('grid')}
                className={`px-3 py-2 transition-colors ${viewMode === 'grid' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400' : 'bg-white dark:bg-slate-900 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                <Squares2X2Icon className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('list')}
                className={`px-3 py-2 transition-colors border-l border-slate-200 dark:border-slate-700 ${viewMode === 'list' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400' : 'bg-white dark:bg-slate-900 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                <ListBulletIcon className="w-4 h-4" />
              </button>
            </div>
            <span className="text-[11px] font-mono font-semibold text-slate-400 dark:text-slate-500 self-center whitespace-nowrap uppercase tracking-wider">
              {filtered.length}/{allProducts.length} réf.
            </span>
          </div>

          {/* Chips Filter Row */}
          <div className="flex flex-wrap items-center gap-2 pt-3 mt-3 border-t border-slate-100 dark:border-slate-800">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500 mr-1">
              Contraintes :
            </span>
            {[
              { id: 'dangerous', label: 'Dangereux (ADR)', icon: ExclamationTriangleIcon },
              { id: 'cold', label: 'Température (Froid)', icon: Thermometer },
              { id: 'non_stackable', label: 'Non-empilable', icon: Layers },
              { id: 'must_be_on_floor', label: 'Au sol uniquement', icon: LayerArrowDown },
              { id: 'rotatable', label: 'Rotatable', icon: Rotate3D },
            ].map(chip => {
              const isSelected = selectedAttributes.includes(chip.id)
              const Icon = chip.icon
              return (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => toggleAttribute(chip.id)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition-all border ${isSelected
                    ? 'bg-orange-500 text-white border-orange-600 shadow-sm shadow-orange-500/20'
                    : 'bg-slate-50 dark:bg-slate-800/80 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-slate-400'}`} />
                  <span>{chip.label}</span>
                </button>
              )
            })}

            {(selectedAttributes.length > 0 || search || filterStatus !== 'all') && (
              <button
                type="button"
                onClick={() => {
                  setSelectedAttributes([])
                  setSearch('')
                  setFilterStatus('all')
                }}
                className="text-xs text-orange-600 dark:text-orange-400 hover:underline font-semibold ml-auto flex items-center gap-1"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                Réinitialiser filtres
              </button>
            )}
          </div>
        </div>

        {/* Product Grid or List */}
        {filtered.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
              <CubeIcon className="w-8 h-8 text-gray-300 dark:text-gray-600" />
            </div>
            <p className="font-semibold text-gray-400 dark:text-gray-500">Aucun produit trouvé</p>
            <p className="text-sm text-gray-400 dark:text-gray-600 mt-1">Créez votre premier produit ou modifiez les filtres.</p>
            <button disabled={!rights.create} onClick={() => { setEditingProduct(null); setIsModalOpen(true) }}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-semibold shadow hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <PlusIcon className="w-4 h-4" />Nouveau produit
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((p: Product) => (
              <ProductCard key={p.id} product={p} rights={rights}
                onEdit={() => { setEditingProduct(p); setIsModalOpen(true) }}
                onDuplicate={() => duplicateMutation.mutate(p)}
                onDelete={() => setDeleteTarget(p)} />
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                  {['Produit', 'SKU', 'Dimensions', 'Poids', 'Contraintes', 'Favoris', 'Statut', 'Actions'].map(h => (
                    <th key={h} className={`py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {filtered.map((p: Product) => (
                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors group">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (p.color || '#6366f1') + '22' }}>
                          <CubeIcon className="w-4 h-4" style={{ color: p.color || '#6366f1' }} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-sm">{p.name}</p>
                          {p.description && <p className="text-xs text-gray-400 truncate max-w-[180px]">{p.description}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-xs font-mono text-gray-500 dark:text-gray-400">{p.sku || '—'}</td>
                    <td className="py-3 px-4 text-xs text-gray-600 dark:text-gray-400">
                      {p.length && p.width && p.height ? (
                        <span className="text-xs font-mono tabular-nums text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-md">
                          <Dimensions length={p.length} width={p.width} height={p.height} from={p.dimension_unit || 'mm'} quantity="vehicleLength" />
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600 dark:text-gray-400">
                      {<Measure value={p.weight} from={p.weight_unit || 'kg'} quantity="mass" />}
                    </td>
                    <td className="py-3 px-4">
                      <ConstraintBadges product={p} />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <StarRating rating={p.favorite_rating || 0} readonly size="sm" />
                        {(p.favorite_rating || 0) > 0 && (
                          <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-md">
                            {p.favorite_rating}/5
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.status === 'active' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : p.status === 'draft' ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                        {p.status === 'active' ? 'Actif' : p.status === 'draft' ? 'Brouillon' : 'Inactif'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button disabled={!canWrite} onClick={() => { setEditingProduct(p); setIsModalOpen(true) }}
                          className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button disabled={!rights.create} onClick={() => duplicateMutation.mutate(p)}
                          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        </button>
                        <button disabled={!rights.delete} onClick={() => setDeleteTarget(p)}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modals */}
        {isModalOpen && (editingProduct ? rights.update : rights.create) && (
          <ProductForm
            key={editingProduct?.id || 'new'}
            product={editingProduct}
            onSave={(data) => {
              if (editingProduct?.id) updateMutation.mutate({ id: editingProduct.id, product: data })
              else createMutation.mutate(data as Omit<Product, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>)
            }}
            onClose={() => { setIsModalOpen(false); setEditingProduct(null) }}
            adrClasses={adrClasses}
            unNumbers={unNumbers}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        )}

        {deleteTarget && rights.delete && (
          <DeleteDialog
            name={deleteTarget.name}
            onConfirm={() => deleteMutation.mutate(deleteTarget.id!)}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </div>
  )
}
